import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Layout } from './components/Layout'
import { HostTree } from './components/HostTree'
import { ServiceDetail } from './components/ServiceDetail'
import { DeployPanel } from './components/DeployPanel'
import { ReelIntel } from './components/ReelIntel'
import { LogsPanel } from './components/LogsPanel'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ProbeGrid } from './components/ProbeGrid'
import { AttentionStrip } from './components/AttentionStrip'
import type { Deployment, Host, Service } from './types'
import { applyProbes, fetchHealth, logsFromProbes, probeUrl, fetchHostInventory, deleteForgeJob, isForgeJobName, type LogEntry, type ProbeResult } from './lib/api'

interface EmpireData {
  hosts: Record<string, Host>
  external: Record<string, Service>
  deployments: Record<string, Deployment>
  lastUpdate: number
}

const EMPTY_HOSTS: Record<string, Host> = {}

const MOCK_EXTERNAL: Record<string, Service> = {
  'memory-vault': { name: 'memory-vault', host: 'memory.demiggwp.me', port: 443, status: 'unknown', url: 'https://memory.demiggwp.me', health_endpoint: '', last_check: Date.now(), metadata: {} },
  'empire-control': { name: 'empire-control', host: 'empire-control.demiggwp.me', port: 443, status: 'unknown', url: 'https://empire-control.demiggwp.me', health_endpoint: '/api/health', last_check: Date.now(), metadata: {} },
}

const MOCK_DEPLOYMENTS: Record<string, Deployment> = {
  'empire-control': { name: 'empire-control', project_path: '/home/demiggwp/projects/empire-control/web', target: 'cloudflare-pages', status: 'deployed', url: 'https://empire-control.demiggwp.me', last_deploy: Date.now(), logs: [] },
  'slk-campaign': { name: 'slk-campaign', project_path: '/home/demiggwp/projects/slk-campaign', target: 'cloudflare-pages', status: 'deployed', url: 'https://slk-campaign.pages.dev', last_deploy: Date.now(), logs: [] },
  'slk-party': { name: 'slk-party', project_path: '/home/demiggwp/projects/slk-party', target: 'cloudflare-pages', status: 'deployed', url: 'https://slk.demiggwp.me', last_deploy: Date.now(), logs: [] },
  'gods-gift': { name: 'gods-gift', project_path: '/home/demiggwp/projects/gods-gift', target: 'cloudflare-pages', status: 'deployed', url: 'https://godsgift.demiggwp.me', last_deploy: Date.now(), logs: [] },
  demiggwpgaming: { name: 'demiggwpgaming', project_path: '/home/demiggwp/projects/demiggwpgaming', target: 'cloudflare-pages', status: 'deployed', url: 'https://dev.demiggwp.me', last_deploy: Date.now(), logs: [] },
}

function toast(message: string) {
  window.dispatchEvent(new CustomEvent('empire-toast', { detail: message }))
}

export function App() {
  const [selectedHost, setSelectedHost] = useState('msi-debian')
  const [selectedService, setSelectedService] = useState<Service | null>(null)
  const [viewMode, setViewMode] = useState<'overview' | 'detail'>('overview')
  const [searchOpen, setSearchOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [notice, setNotice] = useState('')
  const [probes, setProbes] = useState<ProbeResult[]>([])
  const [logs, setLogs] = useState<LogEntry[]>([{
    time: new Date().toISOString().replace('T', ' ').slice(0, 19),
    ts: Date.now(),
    level: 'info',
    message: 'Control plane booted. Edge probes run against public hostnames only.',
    source: 'ui',
  }])
  const [data, setData] = useState<EmpireData>({
    hosts: EMPTY_HOSTS,
    external: MOCK_EXTERNAL,
    deployments: MOCK_DEPLOYMENTS,
    lastUpdate: Date.now(),
  })
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const pushLogs = useCallback((entries: LogEntry[]) => {
    setLogs((prev) => [...entries, ...prev].slice(0, 200))
  }, [])

  const refresh = useCallback(async () => {
    setRefreshing(true)
    try {
      const payload = await fetchHealth()
      setProbes(payload.results)
      setData((prev) => {
        const next = applyProbes(prev.hosts, prev.external, prev.deployments, payload.results)
        return { ...next, lastUpdate: payload.now }
      })
      pushLogs(logsFromProbes(payload.results))
      setNotice(`Probed ${payload.healthy}/${payload.total} public targets from ${payload.vantage}`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'health failed'
      pushLogs([{ time: new Date().toISOString().replace('T', ' ').slice(0, 19), ts: Date.now(), level: 'error', message: msg, source: 'ui' }])
      setNotice(msg)
    } finally {
      setRefreshing(false)
    }
  }, [pushLogs])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, 30000)
    return () => clearInterval(interval)
  }, [refresh])

  useEffect(() => {
    let cancelled = false
    fetchHostInventory()
      .then((inv) => {
        if (cancelled) return
        setData((prev) => ({
          ...prev,
          hosts: inv.hosts,
          external: inv.external || prev.external,
          deployments: inv.deployments || prev.deployments,
        }))
        const n = Object.values(inv.hosts || {}).reduce((acc, h) => acc + (h.services?.length || 0), 0)
        setNotice(`Inventory: ${n} LAN services from msi-debian`)
        pushLogs([{
          time: new Date().toISOString().replace('T', ' ').slice(0, 19),
          ts: Date.now(),
          level: 'info',
          message: `Loaded discovered inventory (${n} services). LAN stays lan-unprobed from edge.`,
          source: 'inventory',
        }])
      })
      .catch((err) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : 'inventory failed'
        pushLogs([{
          time: new Date().toISOString().replace('T', ' ').slice(0, 19),
          ts: Date.now(),
          level: 'error',
          message: msg,
          source: 'inventory',
        }])
      })
    return () => { cancelled = true }
  }, [pushLogs])

  useEffect(() => {
    const onToast = (e: Event) => setNotice((e as CustomEvent<string>).detail)
    window.addEventListener('empire-toast', onToast)
    return () => window.removeEventListener('empire-toast', onToast)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault()
        refresh()
      } else if (e.key === '/') {
        e.preventDefault()
        setSearchOpen(true)
      } else if (e.key === 'Escape') {
        setSearchOpen(false)
        if (viewMode === 'detail') {
          setSelectedService(null)
          setViewMode('overview')
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [refresh, viewMode])

  const handleDeleteForgeJob = async (service: Service) => {
    if (!isForgeJobName(service.name)) {
      toast('Only forge-job-* can be deleted')
      return
    }
    const ok = window.confirm(`Delete ${service.name}?\n\nThis will stop the unit, disable it, delete the unit file, delete ~/AI/forge/jobs/<id>/, and drop tunnel/DNS if present.`)
    if (!ok) return
    setDeletingId(service.name)
    try {
      const result = await deleteForgeJob(service.name)
      if (result.success) {
        toast(`${service.name} deleted`)
        pushLogs([{ time: new Date().toISOString().replace('T', ' ').slice(0, 19), ts: Date.now(), level: 'success', message: `Deleted ${service.name}`, source: 'delete' }])
        setData((prev) => {
          const nextHosts: Record<string, Host> = {}
          for (const [k, h] of Object.entries(prev.hosts)) {
            nextHosts[k] = { ...h, services: h.services.filter((s) => s.name !== service.name) }
          }
          return { ...prev, hosts: nextHosts }
        })
        if (selectedService?.name === service.name) {
          setSelectedService(null)
          setViewMode('overview')
        }
      } else {
        const msg = result.errors.join('; ') || 'delete failed'
        toast(msg)
        pushLogs([{ time: new Date().toISOString().replace('T', ' ').slice(0, 19), ts: Date.now(), level: 'error', message: `${service.name}: ${msg}`, source: 'delete' }])
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  const handleServiceClick = (service: Service) => {
    setSelectedService(service)
    setViewMode('detail')
  }

  const handleBack = () => {
    setSelectedService(null)
    setViewMode('overview')
  }

  const handleProbeService = async (service: Service) => {
    if (!service.url.startsWith('https://')) {
      toast('LAN services cannot be probed from Cloudflare edge')
      return
    }
    try {
      const result = await probeUrl(service.url)
      setSelectedService({
        ...service,
        status: result.status,
        last_check: Date.now(),
        metadata: { ...service.metadata, http: result.http, ms: result.ms, vantage: 'cloudflare-edge' },
      })
      pushLogs(logsFromProbes([result]))
      toast(`${service.name}: ${result.status} HTTP ${result.http}`)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'probe failed')
    }
  }

  const handleSearchSelect = (service: Service) => {
    setSearchOpen(false)
    setSearch('')
    handleServiceClick(service)
  }

  const allServices = useMemo(() => {
    const list: Service[] = [
      ...Object.values(data.hosts).flatMap((h) => h.services),
      ...Object.values(data.external),
    ]
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((s) => `${s.name} ${s.host} ${s.url} ${s.port}`.toLowerCase().includes(q))
  }, [data, search])

  return (
    <Layout>
      <Header
        lastUpdate={data.lastUpdate}
        probes={probes}
        refreshing={refreshing}
        notice={notice}
      />

      <AttentionStrip probes={probes} />

      <ProbeGrid probes={probes} loading={refreshing && probes.length === 0} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <HostTree
            hosts={data.hosts}
            external={data.external}
            selectedHost={selectedHost}
            onHostSelect={setSelectedHost}
            onServiceSelect={handleServiceClick}
            onDeleteForgeJob={handleDeleteForgeJob}
            deletingId={deletingId}
          />
        </div>

        <div className="lg:col-span-3 space-y-4">
          {viewMode === 'overview' ? (
            <>
              <ReelIntel />
              <DeployPanel deployments={data.deployments} onLog={(msg) => pushLogs([{ time: new Date().toISOString().replace('T', ' ').slice(0, 19), ts: Date.now(), level: 'info', message: msg, source: 'ui' }])} />
              <LogsPanel className="min-h-64" logs={logs} />
            </>
          ) : (
            <>
              <ServiceDetail
                service={selectedService!}
                onProbe={(url) => {
                  if (!url.startsWith('https://')) {
                    toast('LAN services cannot be probed from Cloudflare edge')
                    return
                  }
                  probeUrl(url).then(result => {
                    setSelectedService({
                      ...selectedService!,
                      status: result.status,
                      last_check: Date.now(),
                      metadata: { ...selectedService!.metadata, http: result.http, ms: result.ms, vantage: 'cloudflare-edge' },
                    })
                    pushLogs(logsFromProbes([result]))
                    toast(`${selectedService!.name}: ${result.status} HTTP ${result.http}`)
                  }).catch(err => {
                    toast(err instanceof Error ? err.message : 'probe failed')
                  })
                }}
                onShowLogs={() => {
                  const el = document.getElementById('activity-log')
                  el?.scrollIntoView({ behavior: 'smooth' })
                }}
                onDelete={handleDeleteForgeJob}
                deleting={deletingId === selectedService?.name}
              />
              <LogsPanel className="min-h-64" logs={logs} />
            </>
          )}
        </div>
      </div>

      <Footer
        onBack={handleBack}
        viewMode={viewMode}
        onRefresh={refresh}
        onSearch={() => setSearchOpen(true)}
        refreshing={refreshing}
      />

      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-24" onClick={() => setSearchOpen(false)}>
          <div className="w-full max-w-xl bg-[#111827] border border-[#374151] rounded-xl p-4" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search services, hosts, URLs…"
              className="w-full bg-[#0a0f1a] border border-[#374151] rounded-lg px-3 py-2 font-mono text-sm outline-none focus:border-[#06b6d4]"
            />
            <div className="mt-3 max-h-80 overflow-y-auto space-y-1">
              {allServices.slice(0, 20).map((svc) => (
                <button
                  type="button"
                  key={`${svc.name}-${svc.url}`}
                  onClick={() => handleSearchSelect(svc)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1f2937] font-mono text-sm"
                >
                  <div>{svc.name}</div>
                  <div className="text-xs text-[#6b7280]">{svc.url} · {svc.status}</div>
                </button>
              ))}
              {allServices.length === 0 && <div className="text-sm text-[#6b7280] px-3 py-2">No matches</div>}
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
